import { apiClient } from '@/infrastructure/api'
import {
  DashboardAnalyticsApi,
  DashboardAnalyticsApiPeriodReturnRateRequest,
  DashboardAnalyticsApiCountryReturnRateRequest,
  DashboardAnalyticsApiReturnsMainViewRequest,
  DashboardAnalyticsApiGetItemsAndTotalsPerDayRequest,
  DashboardAnalyticsApiReturnsByCountryRequest,
  DashboardAnalyticsApiProductMainViewRequest,
  DashboardAnalyticsApiReturnRateTableRequest,
  DashboardAnalyticsApiFindProductsRequest,
  DashboardAnalyticsApiProductDetailViewRequest,
  DashboardAnalyticsApiReturnRateByProductRequest,
  DashboardAnalyticsApiReturnRateByProductCountryRequest,
  DashboardAnalyticsApiGetAvailableFiltersRequest
} from '@itsrever/rever-api'
import {
  toCountriesReturnRate,
  toCountryReturns,
  toPeriodReturnRate,
  toPeriodReturns,
  toReturnMetrics,
  toProductMetrics,
  toProductsReturnRate,
  toProducts,
  toProductDetails,
  toProductReturnRate,
  toProductCountryReturnRate,
  toFilters,
  toScopePortals
} from './dto'

export const analyticsApi = new DashboardAnalyticsApi(
  undefined,
  undefined,
  apiClient
)

async function getPeriodReturnRate(
  args: DashboardAnalyticsApiPeriodReturnRateRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.periodReturnRate(args, { signal })
  return toPeriodReturnRate(result.data.aggregated_period_return_rates ?? [])
}

async function getCountriesReturnRate(
  args: DashboardAnalyticsApiCountryReturnRateRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.countryReturnRate(args, { signal })

  return toCountriesReturnRate(result.data.entries ?? [])
}

async function getReturnMetrics(
  args: DashboardAnalyticsApiReturnsMainViewRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.returnsMainView(args, { signal })
  return toReturnMetrics(result.data)
}

async function getDailyReturns(
  args: DashboardAnalyticsApiGetItemsAndTotalsPerDayRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.getItemsAndTotalsPerDay(args, { signal })
  return toPeriodReturns(result.data)
}

async function getCountriesReturns(
  args: DashboardAnalyticsApiReturnsByCountryRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.returnsByCountry(args, { signal })
  return toCountryReturns(result.data)
}

async function getProductsMetrics(
  args: DashboardAnalyticsApiProductMainViewRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.productMainView(args, { signal })
  return toProductMetrics(result.data)
}

async function getProductsReturnRate(
  args: DashboardAnalyticsApiReturnRateTableRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.returnRateTable(args, { signal })
  return toProductsReturnRate(result.data)
}

async function findProducts(
  args: DashboardAnalyticsApiFindProductsRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.findProducts(args, { signal })
  return toProducts(result.data)
}

async function getProductDetails(
  args: DashboardAnalyticsApiProductDetailViewRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.productDetailView(args, {
    signal
  })
  return toProductDetails(result.data)
}

async function getProductReturnRate(
  args: DashboardAnalyticsApiReturnRateByProductRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.returnRateByProduct(args, { signal })
  return toProductReturnRate(result.data.aggregated_product_return_rates ?? [])
}

async function getProductCountryReturnRate(
  args: DashboardAnalyticsApiReturnRateByProductCountryRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.returnRateByProductCountry(args, {
    signal
  })
  return toProductCountryReturnRate(result.data.country_return_rates ?? [])
}

async function getAnalyticsFilters(
  args: DashboardAnalyticsApiGetAvailableFiltersRequest,
  signal?: AbortSignal
) {
  const result = await analyticsApi.getAvailableFilters(args, { signal })
  return toFilters(result.data)
}

// getScopePortals lists the caller's own portals for the selector. The window is the one on
// screen, so has_sales_data answers for the period being looked at rather than a fixed one.
async function getScopePortals(
  args: { startDate?: string; endDate?: string; group?: string },
  signal?: AbortSignal
) {
  const result = await analyticsApi.getScopePortals(
    { startDate: args.startDate, endDate: args.endDate, group: args.group },
    { signal }
  )
  return toScopePortals(result.data.portals ?? [])
}

export default {
  getPeriodReturnRate,
  getCountriesReturnRate,
  getReturnMetrics,
  getDailyReturns,
  getCountriesReturns,
  getProductsMetrics,
  getProductsReturnRate,
  findProducts,
  getProductDetails,
  getProductReturnRate,
  getProductCountryReturnRate,
  getAnalyticsFilters,
  getScopePortals
}
